import { showReportDialog } from '@sentry/browser';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/nextjs';
import styled from 'styled-components';
import { IChildrenProps } from '../../models/utilTypes';
import StyledButton, { ButtonTheme } from '../../ui/Button/StyledButton';
import CardHeader from '../../ui/Card/CardHeader';
import StyledCard from '../../ui/Card/StyledCard';
import LayoutManager, { ColumnLayout } from '../GlobalLayout/LayoutManager';

const StyledContainer = styled.div`
  padding: 16px 24px;

  .actions {
    margin: 16px 0 0;
    display: flex;
    gap: 16px;
  }
`;

const StyledDetails = styled.details`
  padding: 16px 24px;

  border-top: 1px solid #d9dbde;

  summary {
    cursor: pointer;
  }

  & > p {
    margin: 16px 0 0;
  }
  & > p:first-of-type {
    font-weight: 700;
    margin: 16px 0;
  }
`;

interface IErrorBoundaryProps extends IChildrenProps {
  includeLayoutManager?: boolean;
}

// https://www.smashingmagazine.com/2020/06/react-error-handling-reporting-error-boundary-sentry/
// the page gets 'stuck' showing an error once an error is tripped.
// We could probably hook into the next/router to untrip it IF the user tries to navigate to something new
// Issue for another day...
const ErrorBoundary: React.FC<IErrorBoundaryProps> = ({
  children,
  includeLayoutManager = true
}) => (
  // @ts-ignore
  <SentryErrorBoundary
    fallback={({ error, componentStack, resetError, eventId, ...rest }) => {
      const card = (
        <StyledCard>
          <CardHeader as="h1" title="Wide Right!" />
          <StyledContainer>
            <p>
              Unfortunately, something went wrong. We've recorded the bug, but
              please leave additional feedback to give us more context!
            </p>

            <div className="actions">
              <StyledButton
                $colorScheme={ButtonTheme.Theme}
                onClick={() => {
                  showReportDialog({ eventId: eventId ?? undefined });
                }}
              >
                Report feedback
              </StyledButton>
              <StyledButton
                $colorScheme={ButtonTheme.Theme}
                onClick={() => {
                  resetError();
                }}
              >
                Click here to reset!
              </StyledButton>
            </div>
          </StyledContainer>
          <StyledDetails as="details">
            <summary>Full details</summary>
            <p>{error.toString()}</p>
            <p>{componentStack}</p>
          </StyledDetails>
        </StyledCard>
      );

      console.log(
        'ErrorBoundary',
        'fallback',
        'error:',
        error,
        'rest keys:',
        Object.keys(rest)
      );

      if (includeLayoutManager) {
        return (
          <LayoutManager
            columnLayout={ColumnLayout.SingleNarrow}
            pageConfig={undefined as any} // Not needed for Taboola
          >
            {card}
          </LayoutManager>
        );
      }

      return card;
    }}
  >
    {children}
  </SentryErrorBoundary>
);

export default ErrorBoundary;
